/**
 * Decide whether a router navigation on a *server-rendered* (legacy Twig) page must be handed off
 * to a real full page load instead of a client-side vue-router transition.
 *
 * Why this exists: base.html.twig mounts the Vue SPA on every page, but legacy Twig pages override
 * `block body` so they never emit the `#spa-shell` marker — which means App.vue's `isInVue` is false
 * and `<router-view>` never renders. A router-link click (top nav, global search) on such a page
 * would otherwise change the URL + title client-side while nothing actually navigates ("the SPA eats
 * the nav"). In that case the caller must do a full page load of the target (see app.js).
 *
 * The `isServerRenderedPage` signal MUST be the presence/absence of `#spa-shell`, NOT `from.name`.
 * Legacy Twig pages can also match a registered SPA route (routes.js), so `from.name`
 * is set even though we're on the Twig page — the old `from.name === null` check silently failed for
 * them (the regression this guards against).
 *
 * @param {boolean} isServerRenderedPage - true when the document is a legacy Twig page (no #spa-shell)
 * @param {{name: ?string, path: string, fullPath: string}} to   - vue-router target route
 * @param {{name: ?string, path: string, fullPath: string}} from - vue-router current route
 * @returns {boolean} true => caller should do a full page load; false => let vue-router handle it
 */
export function shouldHandOffToFullLoad(isServerRenderedPage, to, from) {
  return (
    isServerRenderedPage &&
    // Skip the SPA's own initial route resolution on mount (START_LOCATION fullPath is "/").
    from.fullPath.length > 1 &&
    // Only hand off to a real, named Vue route.
    !!to.name &&
    // Skip in-page navigation (hash tabs, edit drawers that keep the same path).
    to.path !== from.path
  );
}
